import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared/enum";

import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import EfficiencyMapCardConfiguration from "@app/types/dashboard/cards/efficiencyMap/EfficiencyMapCardConfiguration";
import EntityTypesSupportedInEfficiencyMap from "@app/types/dashboard/cards/efficiencyMap/EntityTypesSupportedInEfficiencyMap";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import { createEmptyFilterModel } from "@app/util/filters/utils";

import {
  applyEfficiencyMapDrillDownListViewSettings,
  EfficiencyMapCardActions,
  fetchEfficiencyMapCardDrillDownData,
  hideEfficiencyMapDrillDown,
  showEfficiencyMapDrillDown,
} from "./actions";

export interface EfficiencyMapCardState {
  configuration?: EfficiencyMapCardConfiguration; // also works as a "visible" trigger, visible if set
  loading?: boolean;
  totalFilteredRecords: Record<EntityTypesSupportedInEfficiencyMap, number>;
  totalRecords: Record<EntityTypesSupportedInEfficiencyMap, number>;
  viewStates: Record<EntityTypesSupportedInEfficiencyMap, DrillDownViewState>;
}

export const efficiencyMapInitialState: EfficiencyMapCardState = {
  totalFilteredRecords: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInEfficiencyMap, number>
  ),
  totalRecords: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInEfficiencyMap, number>
  ),
  viewStates: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => {
      result[entityType] = {
        columns: [],
        filter: createEmptyFilterModel(),
        range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
        sort: [],
      };
      return result;
    },
    {} as Record<EntityTypesSupportedInEfficiencyMap, DrillDownViewState>
  ),
};

const efficiencyMapState = createReducer<EfficiencyMapCardState, EfficiencyMapCardActions>(
  efficiencyMapInitialState
)
  .handleAction(showEfficiencyMapDrillDown.request, (state, { payload }) => ({
    ...state,
    configuration: payload.configuration,
  }))
  .handleAction(showEfficiencyMapDrillDown.success, (state, { payload }) => ({
    ...state,
    viewStates: payload.viewStates,
  }))
  .handleAction(hideEfficiencyMapDrillDown, (state) => ({ ...state, configuration: undefined }))
  .handleAction(fetchEfficiencyMapCardDrillDownData.request, (state) => ({
    ...state,
    loading: true,
  }))
  .handleAction(fetchEfficiencyMapCardDrillDownData.success, (state, { payload }) => ({
    ...state,
    loading: false,
    totalFilteredRecords: {
      ...state.totalFilteredRecords,
      [payload.entityType]: payload.totalFilteredRecords,
    },
    totalRecords: {
      ...state.totalRecords,
      [payload.entityType]: payload.totalRecords,
    },
  }))
  .handleAction(fetchEfficiencyMapCardDrillDownData.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(applyEfficiencyMapDrillDownListViewSettings, (state, { payload }) => {
    const viewState = state.viewStates[payload.entityType];
    return {
      ...state,
      viewStates: {
        ...state.viewStates,
        [payload.entityType]: {
          ...viewState,
          columns: payload.columns ?? viewState.columns,
          filter: payload.filter ?? viewState.filter,
          range: payload.range ?? viewState.range,
          sort: payload.sort ?? viewState.sort,
        },
      },
    };
  });

export default efficiencyMapState;
